import pathLib from 'path-browserify';

import { leaderboardClient } from '@neptune/shared/core-apis-leaderboard-domain';

import {
  AttributeDefinition,
  AttributeDefinitionConverter,
  AttributeType,
} from 'domain/experiment/attribute';

export type ListAttributeDefinitionsRequest = {
  identifier: string;
  attributeTypes?: AttributeType[];
  limit?: number;
  offset?: number;
  prefix?: string;
};

export type ListAttributeDefinitionsResult = {
  attributeDefinitions: AttributeDefinition[];
  subDirectoriesPaths: string[];
  totalCount: number;
  hasMore: boolean;
  size: number;
  offset?: number;
  limit?: number;
};

export async function listAttributeDefinitions({
  identifier,
  attributeTypes,
  limit,
  offset,
  prefix,
}: ListAttributeDefinitionsRequest): Promise<ListAttributeDefinitionsResult> {
  const result = await leaderboardClient.queryAttributeDefinitions({
    experimentIdentifier: identifier,
    attributeType: attributeTypes
      ?.map(AttributeDefinitionConverter.attributeTypeToApi)
      .map((at) => at?.valueOf())
      .filter((s): s is string => !!s),
    limit,
    offset,
    prefix: normalizePrefix(prefix),
  });

  const subDirectoriesPaths = result.subPaths?.map((subPath) => {
    if (!prefix) {
      return subPath;
    }

    // For nested levels, combine prefix with subPath
    // prefix is like "sys/" and subPath is like "metrics"
    // result should be "sys/metrics"
    return pathLib.join(prefix, subPath);
  });

  const attributeDefinitions = result.entries.map(
    AttributeDefinitionConverter.attributeDefinitionFromApiToDomain,
  );

  const hasMore =
    limit !== undefined && offset !== undefined
      ? offset + result.entries.length + (result.subPaths?.length ?? 0) < result.totalCount
      : false;

  return {
    attributeDefinitions,
    subDirectoriesPaths: subDirectoriesPaths ?? [],
    totalCount: result.totalCount,
    hasMore,
    offset,
    size: attributeDefinitions.length + (subDirectoriesPaths?.length ?? 0),
    limit,
  };
}

function normalizePrefix(prefix?: string) {
  if (prefix === undefined) {
    return undefined;
  }

  if (prefix === '.' || prefix === '') {
    return '';
  }

  return pathLib.normalize(`${prefix}/`);
}
